<template>
  <pui-flex
    :class="rootClass"
    direction="column"
    @dragenter.stop="onDragEnter"
    @dragover.stop="onDragOver"
    @dragleave.stop="onDragLeave"
    @drop.stop.prevent="onDrop"
  >
    <template v-if="hasError">
      <pui-flex class="file-classifications-tab__error" direction="column" alignItems="center">
        <i class="mdi mdi-close-circle-outline error-icon"></i>
        <i18n
          class="msg msg-primary"
          path="dam.files.panel_selection.attributes.tabs.classifications.error.primary"/>
        <i18n
          class="msg msg-secondary"
          path="dam.files.panel_selection.attributes.tabs.classifications.error.secondary"
        >
          <template #button>
            <i18n
              class="reload-btn"
              @click="reloadAfterError"
              path="dam.files.panel_selection.attributes.tabs.classifications.error.button"
            ></i18n>
          </template> </i18n
      ></pui-flex>
    </template>
    <template v-else-if="commonPaths.length">
      <pui-flex
        v-for="path in commonPaths"
        :key="path"
        alignItems="center"
        class="file-classifications-tab__path-wrapper"
      >
        <pui-folder-breadcrumbs
          class="file-classifications-tab__breadcrumbs"
          :path="path"
          :lastN="-1"
          @error="onFolderLoadError"
        ></pui-folder-breadcrumbs>
        <pui-button
          variant="transparent"
          picto="mdi-close"
          class="file-classifications-tab__close-btn"
          @click="removePath(path)"
        ></pui-button>
      </pui-flex>
    </template>
    <template v-else>
      <pui-flex
        class="file-classifications-tab__no-common-paths"
        direction="column"
        alignItems="center"
      >
        <i18n
          class="msg msg-primary"
          path="dam.files.panel_selection.attributes.tabs.classifications.no_common_paths"
        />
      </pui-flex>
    </template>
  </pui-flex>
</template>

<script>
import services from '../../../../../core/services';
import Permissions from '../../../constants/permissions';
import { getFolderIdFromPath } from '../../../utils/folders';
import puiFolderBreadcrumbs from '../FolderBreadcrumbs';

export default {
  components: {
    puiFolderBreadcrumbs,
  },
  props: {
    /**
     * Array with each files who contains attribute values
     */
    sourceFiles: {
      required: true,
    },
    /**
     * Array with each files who contains attribute values
     */
    updatedFiles: {
      required: true,
    },
    /**
     * The folder being dragged
     */
    draggingFolder: {
      type: Object,
      default: null,
    },
  },
  name: 'pui-file-classifications-tab',
  data() {
    return {
      hasError: false,
      commonPaths: [],
      foldersToDissociate: [],
      foldersToAssociate: [],
      // DND
      isBeingDraggedOver: false,
    };
  },
  computed: {
    /**
     * @returns {object} root class object
     */
    rootClass() {
      return {
        'file-classifications-tab file-classifications-tab__root': true,
        // Only set the drag styles when a folder is being dragged
        isBeingDraggedOver: this.isBeingDraggedOver && this.draggingFolder,
      };
    },
    /**
     * @returns {object} the move object of the associate/dissociate changes
     */
    moveObj() {
      const associate = !this.foldersToAssociate.length
        ? []
        : [
            {
              fileIds: this.updatedFiles.map(file => file.itemId),
              folderIds: [...this.foldersToAssociate],
            },
          ];

      const dissociate = !this.foldersToDissociate.length
        ? []
        : [
            {
              fileIds: this.updatedFiles.map(file => file.itemId),
              folderIds: [...this.foldersToDissociate],
            },
          ];

      return {
        associate,
        dissociate,
      };
    },
    /**
     * @returns {boolean} if the user has the permission to move the files
     */
    hasPermission() {
      return services.getService('auth').hasPermission(Permissions.Files.MOVE);
    },
  },
  methods: {
    // FOLDER
    /**
     * Activates the error state
     */
    onFolderLoadError() {
      this.hasError = true;
    },
    /**
     * Removes the error state. The folders will reload
     * automatically on mount
     */
    reloadAfterError() {
      this.hasError = false;
    },
    // PATH
    /**
     * Adds the folder of the path to the list of folders to disssociate
     *
     * @param {string} pathToRemove - the path to the folder to dissociate
     */
    removePath(pathToRemove) {
      if (!this.hasPermission) {
        services
          .getService('alerts')
          .alertError(this.$t('dam.files.move.alert.no_delete_permission'));
        return;
      }

      // No need to recompute, we can directly remove the path from common paths
      this.commonPaths = this.commonPaths.filter(oldPath => oldPath !== pathToRemove);

      // Remove the path from files
      this.updatedFiles.forEach(file => {
        file.paths = file.paths.filter(oldPath => oldPath !== pathToRemove);
      });

      const folderId = getFolderIdFromPath(pathToRemove);
      const associateIdx = folderId ? this.foldersToAssociate.indexOf(folderId) : -1;
      if (associateIdx !== -1) {
        // If we remove a folder that was *going to be* associated, simply
        // remove it from associations
        this.foldersToAssociate.splice(associateIdx, 1);
      } else if (folderId && !this.foldersToDissociate.includes(folderId)) {
        // Otherwise, the folder was already associated, so we should
        // do a dissociation operation
        this.foldersToDissociate.push(folderId);
      }

      this.$emit('moveChanged', this.moveObj);
    },
    /**
     * Adds the folder of the path to the list of folders to associate
     *
     * @param {string} pathToAdd - the path to the folder to associate
     */
    addPath(pathToAdd) {
      if (!this.hasPermission) {
        services
          .getService('alerts')
          .alertError(this.$t('dam.files.move.alert.no_move_permission'));
        return;
      }

      // No need to recompute, we can directly add the path to common paths
      if (!this.commonPaths.includes(pathToAdd)) {
        this.commonPaths.push(pathToAdd);
      }

      // Add the path to files
      this.updatedFiles.forEach(file => {
        if (!file.paths.includes(pathToAdd)) {
          file.paths.push(pathToAdd);
        }
      });

      const folderId = getFolderIdFromPath(pathToAdd);
      const dissociateIdx = folderId ? this.foldersToDissociate.indexOf(folderId) : -1;
      if (dissociateIdx !== -1) {
        // If we add a folder that was *going to be* dissociated, simply
        // remove it from dissociations (meaning we preserve it)
        this.foldersToDissociate.splice(dissociateIdx, 1);
      } else if (folderId && !this.foldersToAssociate.includes(folderId)) {
        // Otherwise, the folder was not already associated, so we should
        // do an association operation
        this.foldersToAssociate.push(folderId);
      }

      this.$emit('moveChanged', this.moveObj);
    },
    /**
     * Computes the common paths of the files
     *
     * @param {boolean} fromSource - whether to compute value from sourceFiles or updatedFiles
     */
    computeCommonPaths(fromSource) {
      const commonPaths = [];
      const files = fromSource ? this.sourceFiles : this.updatedFiles;

      files.forEach((file, fileIndex) => {
        file.paths.forEach(path => {
          // If we already know that this is a common path, skip it
          if (commonPaths.includes(path)) {
            return;
          }

          const isPathCommonToAllOtherFiles = files.every((otherFile, otherFileIndex) => {
            if (fileIndex === otherFileIndex) {
              // Skip if we are comparing the same file
              return true;
            }

            return otherFile.paths.includes(path);
          });

          if (isPathCommonToAllOtherFiles) {
            commonPaths.push(path);
          }
        });
      });

      this.commonPaths = commonPaths;
    },
    // DND
    /**
     * Fired when a drag enters the root
     *
     * @param {*} e - the drag event
     * @returns {void}
     */
    onDragEnter(e) {
      if (!this.draggingFolder) {
        // Do not process dnd events if we are not dragging a folder
        return;
      }
      e.preventDefault(); // Allows drop
      this.isBeingDraggedOver = true;
    },
    /**
     * Fired when a drag happens over the root
     * or a child
     *
     * @param {*} e - the drag event
     * @returns {void}
     */
    onDragOver(e) {
      if (!this.draggingFolder) {
        // Do not process dnd events if we are not dragging a folder
        return;
      }
      e.preventDefault(); // Allows drop
      // If the user drags over a child, dragenter will
      // not be fired, but we still want to capture drops
      this.isBeingDraggedOver = true;

      // We must add an effect for the drop to trigger
      e.dataTransfer.dropEffect = 'copy';

      return false; // Prevent default action of browser. For compatibility
    },
    /**
     * Fired when a drag leaves the root
     *
     * @param {*} e - the drag event
     * @returns {void}
     */
    onDragLeave(e) {
      this.isBeingDraggedOver = false;
    },
    /**
     * Fired when a drag is dropped on the root
     *
     * @param {*} e - the drop event
     */
    onDrop(e) {
      // "Save" folder to local var since it
      // will be cleared once drag ends
      const draggingFolder = this.draggingFolder;
      this.addPath(draggingFolder.paths[0]);
    },
    // EXPOSED
    /**
     * Resets state and recomputes the common paths
     *
     * @param {boolean} fromSource - whether to compute value from sourceFiles or updatedFiles
     */
    resetComputeValues(fromSource) {
      this.foldersToDissociate = [];
      this.foldersToAssociate = [];
      this.computeCommonPaths(fromSource);
    },
  },
  /**
   * Component mounted hook
   */
  mounted() {
    this.computeCommonPaths(false);
  },
};
</script>
